import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PrizeWheel, { mapBackendPrizeToFrontend } from './PrizeWheel';
import { getLevelIcon } from 'components/rewards/levelHeader/levelHeader';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import VipCardSkeleton from './VipCardSkeleton';
import { setLoginOpen } from 'store/uiSlice';
import useLocalizedNavigate from 'hooks/useLocalizedNavigate';
import styles from './DailySpinWidget.module.scss';

const DailySpinWidget: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const dispatch = useAppDispatch();
  const { isAuthenticated, user, isLoading } = useAppSelector((state) => state.auth);
  const wheelPrizes = useAppSelector((state) => state.site.wheelPrizes);
  const siteConfig = useAppSelector((state) => state.site.config);

  const rawPrizes = wheelPrizes ?? siteConfig?.siteWheel?.prizes ?? [];
  const prizes = useMemo(() => {
    return rawPrizes.map(mapBackendPrizeToFrontend);
  }, [rawPrizes]);

  const handleSpinClick = () => {
    navigate('/daily-spin');
  };

  const tierProgress = user?.level?.tierProgress;
  const currentLevelName = tierProgress?.currentTier?.tierName || 'Crab';
  const nextLevelName = tierProgress?.nextTier?.tierName;
  const progressPercent = tierProgress?.progressPercent ?? 0;
  const currentPoints = tierProgress?.currentPoints ?? '0';
  const nextTierMinPoints = tierProgress?.nextTierMinPoints ?? '0';
  const pointsUntilNext = tierProgress?.pointsUntilNextTier ?? '0';

  // If nextTier is null or missing, the user has reached the final level.
  const isMaxLevel = !tierProgress?.nextTier;

  return (
    <div className={styles.widgetContainer}>
      <div className={styles.wheelCard}>
        <div className={styles.halfWheelContainer}>
          <div className={styles.wheelWrapper}>
            {prizes.length > 0 ? (
              <PrizeWheel prizes={prizes} isSpinning={false} isHalfWheel={true} />
            ) : (
              <div style={{ color: 'var(--text-secondary)' }}>{t('common.loading', 'Loading...')}</div>
            )}
          </div>
        </div>

        <div className={styles.titles}>
          <h3 className={styles.mainTitle}>
            <strong>{t('dailySpinWidget.luckyWheel', 'LUCKY WHEEL')}</strong> -{' '}
            {t('dailySpinWidget.freeDailySpin', 'FREE DAILY SPIN!')}
          </h3>
        </div>

        <div className={styles.actionArea}>
          <button
            className={styles.spinButton}
            onClick={handleSpinClick}
            disabled={prizes.length === 0}
          >
            {t('dailySpinWidget.spinNow', 'Spin Now')}
          </button>
        </div>
      </div>

      <div className={styles.conditionText}>
        {t(
          'dailySpinWidget.conditionText',
          'Make a qualifying deposit to unlock your daily spin',
        )}
      </div>

      <div
        className={`${styles.vipCard} ${!isAuthenticated && !isLoading ? styles.unauthCard : ''}`}
      >
        {isLoading ? (
          <VipCardSkeleton />
        ) : !isAuthenticated ? (
          <div className={styles.unauthOverlay}>
            <div className={styles.lockIcon}>🔒</div>
            <h4>{t('dailySpinWidget.unlockVipTitle', 'Unlock VIP Rewards')}</h4>
            <p>
              {t(
                'dailySpinWidget.unlockVipDesc',
                'Log in to track your level and claim daily spins!',
              )}
            </p>
            <button className={styles.loginBtn} onClick={() => dispatch(setLoginOpen(true))}>
              {t('common.loginNow', 'Login Now')}
            </button>
          </div>
        ) : isMaxLevel ? (
          <div className={styles.maxLevelOverlay}>
            <div className={styles.iconCircle}>{getLevelIcon(currentLevelName)}</div>
            <h4>{t('dailySpinWidget.maxVipReached', 'MAX VIP REACHED')}</h4>
            <p>
              {t('dailySpinWidget.maxVipDesc', {
                level: currentLevelName,
                defaultValue: 'You are at the top! Enjoy your exclusive {{level}} perks.',
              })}
            </p>
          </div>
        ) : (
          <>
            <div className={styles.vipProgress}>
              <div className={styles.vipLevel}>
                <div className={styles.iconCircle}>{getLevelIcon(currentLevelName)}</div>
                <span>{currentLevelName}</span>
              </div>

              <div className={styles.progressBarContainer}>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{ width: `${progressPercent}%` }}
                  ></div>
                  <div
                    className={styles.progressKnob}
                    style={{ left: `${progressPercent}%` }}
                  ></div>
                </div>
                <div className={styles.progressText}>
                  <span className={styles.current}>
                    {t('dailySpinWidget.pointsShort', {
                      count: currentPoints,
                      defaultValue: '{{count}} pts.',
                    })}
                  </span>
                </div>
              </div>

              <div className={styles.vipLevel}>
                <div className={styles.iconCircle}>{getLevelIcon(nextLevelName)}</div>
                <span>{nextLevelName}</span>
              </div>
            </div>

            <div className={styles.wagerInfo}>
              {t('dailySpinWidget.pointInfo', {
                points: pointsUntilNext,
                defaultValue: 'Point Info: {{points}} pts. remaining to next level',
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DailySpinWidget;
